import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Activity, BookOpen, Heart, Pencil, Undo2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import EmptyState from "@/components/EmptyState";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { timeAgo } from "@/lib/timeAgo";
import { jsonRequest } from "@/lib/jsonRequest";
import { formatMoney } from "@/lib/format";

const INITIAL_ROWS = 8;

const SOURCE_LABELS = {
  teacher_classroom: "logged by a teacher",
  teacher_solo: "logged by a teacher",
  parent: "logged by a family",
};

/**
 * Recent donations and reading logs for the class, newest first. Minute
 * logs can be fixed (a typo'd 6 for 60) or undone right here, with the
 * controls always visible so they work on a phone.
 */
export default function ClassroomActivityCard({ activity, editable = true }) {
  const [events, setEvents] = useState(activity);
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? events : events.slice(0, INITIAL_ROWS);

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Recent activity
        </CardTitle>
        <CardDescription>Donations and reading, newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <EmptyState
            bare
            size="sm"
            icon={BookOpen}
            title="No activity yet"
            description="Donations and reading minutes show up here as they come in."
          />
        ) : (
          <>
            <ul className="divide-y divide-border">
              {shown.map((event) =>
                event.kind === "donation" ? (
                  <DonationRow key={event.id} event={event} />
                ) : (
                  <MinuteLogRow
                    key={event.id}
                    event={event}
                    editable={editable}
                    onUpdate={(minutes) => setEvents((rows) => rows.map((row) => (row.id === event.id ? { ...row, minutes } : row)))}
                    onRemove={() => setEvents((rows) => rows.filter((row) => row.id !== event.id))}
                  />
                )
              )}
            </ul>
            {events.length > INITIAL_ROWS && (
              <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowAll(!showAll)}>
                {showAll ? "Show less" : `Show ${events.length - INITIAL_ROWS} more`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StudentName({ event }) {
  if (!event.student_path) return <span className="font-medium">{event.student_display}</span>;
  return (
    <Link href={event.student_path} className="font-medium transition-colors hover:text-primary">
      {event.student_display}
    </Link>
  );
}

function RowIcon({ tone, icon: Icon }) {
  return (
    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone}`}>
      <Icon className="h-4 w-4" />
    </span>
  );
}

function DonationRow({ event }) {
  return (
    <li className="flex items-start gap-3 py-3">
      <RowIcon tone="bg-primary/10 text-primary" icon={Heart} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">
          <span className="font-semibold text-primary">{formatMoney(event.amount_cents)}</span> from{" "}
          <span className="font-medium">{event.donor_name}</span> to <StudentName event={event} />
        </p>
        {event.message && !event.anonymous && (
          <p className="mt-0.5 break-words text-xs italic text-muted-foreground">“{event.message}”</p>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">{timeAgo(event.at)}</p>
      </div>
    </li>
  );
}

function MinuteLogRow({ event, editable, onUpdate, onRemove }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(event.minutes));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canChange = editable && (event.edit_path || event.undo_path);

  const undo = async () => {
    const many = event.student_count > 1;
    const confirmed = await confirm({
      title: many ? `Undo this log for ${event.student_count} students?` : `Undo ${event.minutes} minutes for ${event.student_display}?`,
      body: "The minutes come off their totals. You can log them again if you need to.",
      confirmLabel: "Undo log",
      destructive: true,
    });
    if (!confirmed) return;
    setSaving(true);
    setError("");
    try {
      await jsonRequest(event.undo_path, { method: "DELETE" });
      onRemove();
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    const next = parseInt(draft, 10);
    if (!Number.isFinite(next) || next < 1 || next > 1440) {
      setError("Enter between 1 and 1,440 minutes.");
      return;
    }
    if (next === event.minutes) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await jsonRequest(event.edit_path, { method: "PATCH", data: { minutes: next } });
      onUpdate(result.minutes);
      setEditing(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="flex items-start gap-3 py-3">
      <RowIcon tone="bg-accent/10 text-accent" icon={BookOpen} />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div>
          {editing ? (
            <form onSubmit={save} className="flex flex-wrap items-center gap-2">
              <label htmlFor={`minutes-${event.id}`} className="sr-only">Minutes</label>
              <input
                id={`minutes-${event.id}`}
                type="number"
                min="1"
                max="1440"
                inputMode="numeric"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
                disabled={saving}
                className="h-10 w-24 rounded-lg border border-input bg-card px-3 text-sm font-semibold text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
              <span className="text-sm text-foreground">min · <StudentName event={event} /></span>
            </form>
          ) : (
            <p className="text-sm text-foreground">
              <span className="font-semibold text-accent">{event.minutes} min</span> · <StudentName event={event} />
            </p>
          )}
          {event.note && <p className="mt-0.5 break-words text-xs italic text-muted-foreground">{event.note}</p>}
          <p className={`mt-0.5 text-xs ${error ? "text-destructive" : "text-muted-foreground"}`}>
            {error || `${timeAgo(event.at)} · ${SOURCE_LABELS[event.source] || "logged"}`}
          </p>
        </div>

        {canChange && (
          <div className="-ml-3 flex flex-wrap items-center gap-1">
            {editing ? (
              <>
                <Button type="button" size="sm" variant="outline" className="h-11 sm:h-9" onClick={save} disabled={saving}>
                  <Check className="h-4 w-4" />
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-11 sm:h-9" onClick={() => { setEditing(false); setError(""); }} disabled={saving}>
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {event.edit_path && (
                  <Button type="button" size="sm" variant="ghost" className="h-11 sm:h-9" onClick={() => { setDraft(String(event.minutes)); setEditing(true); }} disabled={saving}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                {event.undo_path && (
                  <Button type="button" size="sm" variant="ghost" className="h-11 sm:h-9" onClick={undo} disabled={saving}>
                    <Undo2 className="h-4 w-4" />
                    {saving ? "Undoing…" : "Undo"}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
